/**
 * What the New request sheet holds, and what it means — `docs/new-request.md`
 * §5.0, §11.3.
 *
 * Pure functions over the sheet's state: which sections are **ready**,
 * **started** or **empty**, what the footer note says, and what Create would
 * be missing. The component draws; this decides, so the rules are tested
 * without a browser.
 */
import {
  BUDGET_BANDS, confirmChannel, dayComma, wantedText, clockBare,
  type ConfirmChannel, type KnownContact, type KnownProperty, type PhoneKind, type Prefers,
  type PropertyCheck, type WantedChoice, type WhenChip,
} from '@rowted/shared'

export const SECTIONS = ['contact', 'property', 'job', 'appointment'] as const
export type SectionKey = (typeof SECTIONS)[number]

export const SECTION_LABELS: Record<SectionKey, string> = {
  contact: 'Contact',
  property: 'Property',
  job: 'Job',
  appointment: 'Appointment',
}

/** The quiet sub-line of a waiting row (§5.0). */
export const SECTION_SUBS: Record<SectionKey, string> = {
  contact: 'find them in the search above — most people are already here',
  property: 'where the work is',
  job: 'title, division, when they want it',
  appointment: 'a site visit with a salesperson',
}

export interface TypedContact {
  firstName: string
  lastName: string
  company: string
  title: string
  phoneKind: PhoneKind
  phone: string
  email: string
  prefers: Prefers
  /** *It's someone else* was pressed for this number (§6.3). */
  allowPhoneMatch: boolean
}

export const EMPTY_TYPED_CONTACT: TypedContact = {
  firstName: '', lastName: '', company: '', title: '', phoneKind: 'mobile', phone: '',
  /* Text, as every board draws a new contact (Boards 2, 4, 14). */
  email: '', prefers: 'sms', allowPhoneMatch: false,
}

export type ContactState =
  | { kind: 'none' }
  /** The seven fields are showing (Add a new contact, or a parsed search). */
  | { kind: 'new'; fields: TypedContact }
  | { kind: 'chosen'; contact: KnownContact }

export interface TypedAddress {
  line1: string
  line2: string
  city: string
  state: string
  postalCode: string
  latitude: number | null
  longitude: number | null
  placeId: string | null
  /** Google confirmed it — *found on the map*. */
  verified: boolean
  /** Typed by hand and the pin kept (§7.1). */
  pinKept: boolean
  /** *It's another unit or lot* — Unit or lot is then required (§7.2). */
  needsUnit: boolean
}

export const EMPTY_ADDRESS: TypedAddress = {
  line1: '', line2: '', city: '', state: '', postalCode: '', latitude: null, longitude: null,
  placeId: null, verified: false, pinKept: false, needsUnit: false,
}

export interface TypedProperty {
  address: TypedAddress
  type: string
  name: string
  nameIsAuto: boolean
  zoneId: string | null
  orgUnitId: string | null
  check: PropertyCheck | null
  /** *Use Kim Residence* was declined for this address. */
  anotherUnit: boolean
}

export type PropertyState =
  | { kind: 'none' }
  | { kind: 'new'; property: TypedProperty }
  | { kind: 'chosen'; property: KnownProperty }

export interface JobFields {
  title: string
  divisionCode: string | null
  leadSourceId: string | null
  newLeadSource: string | null
  /** Filled from their last job (§8): the hint says so until it is changed. */
  leadSourceFromHistory: boolean
  budgetKey: string
  wanted: WantedChoice | null
  /** The chip key, `season:winter-2026` or `pick` (with `wanted.kind` the mode). */
  wantedChip: string | null
}

export const EMPTY_JOB: JobFields = {
  title: '', divisionCode: null, leadSourceId: null, newLeadSource: null,
  leadSourceFromHistory: false, budgetKey: 'not_sure', wanted: null, wantedChip: null,
}

export interface AppointmentFields {
  description: string
  /** Null until somebody picks; the division's estimate is the default (§9.1). */
  minutes: number | null
  salespersonId: string | null
  salespersonName: string | null
  /** The chosen time: an instant, and the day and minutes it is in the branch's zone. */
  startsAt: string | null
  date: string | null
  start: number | null
  confirm: boolean
  /** More addresses an email confirmation goes to (Aaron, step 28). */
  confirmAlsoTo: string[]
  /** The email's own opening, typed over the standard one; null is the standard words. */
  confirmMessage: string | null
  /** The standard words as they were when somebody started editing — to notice they moved. */
  confirmMessageFrom: string | null
}

export const EMPTY_APPOINTMENT: AppointmentFields = {
  description: '', minutes: null, salespersonId: null, salespersonName: null,
  startsAt: null, date: null, start: null, confirm: true,
  confirmAlsoTo: [], confirmMessage: null, confirmMessageFrom: null,
}

export interface SheetState {
  contact: ContactState
  property: PropertyState
  job: JobFields
  appointment: AppointmentFields
  /**
   * The contact came with a chosen property (§5.4): Contact is *who at
   * <property>?*, its people as radio rows, rather than a card.
   */
  contactFromProperty: boolean
  /**
   * The property came with a chosen contact (§5.4): Property lists their
   * places as radio cards with *A new property for <first name>*.
   */
  propertyFromContact: boolean
}

export const EMPTY_SHEET: SheetState = {
  contact: { kind: 'none' },
  property: { kind: 'none' },
  job: EMPTY_JOB,
  appointment: EMPTY_APPOINTMENT,
  contactFromProperty: false,
  propertyFromContact: false,
}

// ---------------------------------------------------------------------------
// Ready, started, empty (§5.0)
// ---------------------------------------------------------------------------

export type SectionStatus = 'empty' | 'started' | 'ready'

const filled = (value: string | null | undefined): boolean => Boolean(value && value.trim())

export function contactStatus(state: SheetState): SectionStatus {
  const contact = state.contact
  if (contact.kind === 'chosen') return 'ready'
  if (contact.kind === 'none') return 'empty'
  const fields = contact.fields
  if (filled(fields.firstName) && filled(fields.lastName)) return 'ready'
  const any = [fields.firstName, fields.lastName, fields.company, fields.title, fields.phone,
    fields.email].some(filled)
  return any ? 'started' : 'empty'
}

/** A typed address is enough once Google confirmed it, or the pin was kept. */
export function addressReady(address: TypedAddress): boolean {
  if (!filled(address.line1)) return false
  if (!(address.verified || address.pinKept)) return false
  if (address.latitude === null || address.longitude === null) return false
  if (address.needsUnit && !filled(address.line2)) return false
  return true
}

export function propertyStatus(state: SheetState): SectionStatus {
  const property = state.property
  if (property.kind === 'chosen') return 'ready'
  if (property.kind === 'none') return 'empty'
  if (addressReady(property.property.address)) return 'ready'
  return filled(property.property.address.line1) ? 'started' : 'empty'
}

export function jobStatus(state: SheetState): SectionStatus {
  const job = state.job
  if (filled(job.title) && job.divisionCode) return 'ready'
  /* A lead source filled from their last job is the sheet's doing, not the person's. */
  const chosenSource = Boolean(job.leadSourceId || job.newLeadSource) && !job.leadSourceFromHistory
  const any = filled(job.title) || Boolean(job.divisionCode) || job.budgetKey !== 'not_sure'
    || Boolean(job.wanted) || chosenSource
  return any ? 'started' : 'empty'
}

export function appointmentStatus(state: SheetState): SectionStatus {
  const appointment = state.appointment
  if (appointment.startsAt && appointment.salespersonId) return 'ready'
  const any = filled(appointment.description) || Boolean(appointment.salespersonId)
    || Boolean(appointment.startsAt)
  return any ? 'started' : 'empty'
}

export function statusOf(state: SheetState, section: SectionKey): SectionStatus {
  switch (section) {
    case 'contact': return contactStatus(state)
    case 'property': return propertyStatus(state)
    case 'job': return jobStatus(state)
    case 'appointment': return appointmentStatus(state)
  }
}

export const readyCount = (state: SheetState): number =>
  SECTIONS.filter((section) => statusOf(state, section) === 'ready').length

/** The Appointment section was started (§11.4): *Create, book later* makes it not booked yet. */
export const appointmentStarted = (state: SheetState): boolean =>
  filled(state.appointment.description) || Boolean(state.appointment.salespersonId)

/** Anything typed anywhere — the unsaved-changes question. */
export const anythingTyped = (state: SheetState): boolean =>
  SECTIONS.some((section) => statusOf(state, section) !== 'empty')

// ---------------------------------------------------------------------------
// Names in sentences
// ---------------------------------------------------------------------------

export function contactFirstName(state: SheetState): string | null {
  if (state.contact.kind === 'chosen') return state.contact.contact.firstName
  if (state.contact.kind === 'new') return state.contact.fields.firstName.trim() || null
  return null
}

export function contactName(state: SheetState): string | null {
  if (state.contact.kind === 'chosen') {
    return `${state.contact.contact.firstName} ${state.contact.contact.lastName}`.trim()
  }
  if (state.contact.kind === 'new') {
    const name = `${state.contact.fields.firstName} ${state.contact.fields.lastName}`.trim()
    return name || null
  }
  return null
}

export function propertyName(state: SheetState): string | null {
  if (state.property.kind === 'chosen') return state.property.property.name
  if (state.property.kind === 'new') return state.property.property.name.trim() || null
  return null
}

const firstOf = (name: string | null): string => name?.split(/\s+/)[0] ?? ''

// ---------------------------------------------------------------------------
// The footer note (§5.0, last rule)
// ---------------------------------------------------------------------------

export type FooterNote =
  | { kind: 'keys' }
  | { kind: 'needed'; text: string }
  | { kind: 'creates'; parts: { text: string; strong?: boolean }[] }
  | { kind: 'missing'; text: string }

/**
 * The parts Create is asked for (Aaron, step 28: it makes what is there). A
 * contact always; a place once anything needs one — a job, a visit, an address
 * started, or a contact already here, which on its own would make nothing; a
 * job once a visit is started or the job itself is.
 */
export function needs(state: SheetState): { place: boolean; job: boolean; visit: boolean } {
  const visit = appointmentStatus(state) !== 'empty'
  const job = visit || jobStatus(state) !== 'empty'
  const place = job || propertyStatus(state) !== 'empty' || state.contact.kind === 'chosen'
  return { place, job, visit }
}

export function footerNote(
  state: SheetState,
  shown: { salespersonFirstName: string | null },
): FooterNote {
  if (!anythingTyped(state)) return { kind: 'keys' }
  const want = needs(state)
  const contactReady = contactStatus(state) === 'ready'
  const propertyReady = propertyStatus(state) === 'ready'
  const placeMissing = want.place && !propertyReady
  if (!contactReady || placeMissing) {
    const needed = !contactReady && placeMissing ? 'a contact and an address'
      : !contactReady ? 'a contact' : 'an address'
    return { kind: 'needed', text: `Still needed: ${needed}` }
  }

  /* What Create makes — the words name only what is there to make (Aaron, step 28). */
  const timed = state.appointment.date !== null && state.appointment.start !== null
    && state.appointment.salespersonId !== null
  const when = timed
    ? `${dayComma(state.appointment.date!).split(',')[0]} ${clockLabel(state.appointment.start!)} with ${
      firstOf(shown.salespersonFirstName ?? state.appointment.salespersonName)}`
    : null
  const withJob = jobStatus(state) === 'ready'
  const placeOn = state.property.kind !== 'none' && propertyReady
  const place = state.property.kind === 'chosen' ? propertyName(state)!
    : /residential/i.test(state.property.kind === 'new' ? state.property.property.type : '')
      ? 'the residence' : 'the property'

  const used = [state.contact.kind === 'chosen' ? contactName(state)! : null,
    placeOn && state.property.kind === 'chosen' ? place : null].filter((one): one is string => one !== null)
  const made = [state.contact.kind === 'new' ? firstOf(contactName(state)) : null,
    placeOn && state.property.kind === 'new' ? place : null,
    withJob ? 'the job' : null].filter((one): one is string => one !== null)

  const parts: { text: string; strong?: boolean }[] = []
  if (used.length > 0) {
    parts.push({ text: 'Uses ' })
    used.forEach((name, index) => {
      if (index > 0) parts.push({ text: ' and ' })
      parts.push({ text: name, strong: true })
    })
    if (made.length > 0 || when) {
      parts.push({ text: ` · adds ${listWords(made, Boolean(when))}` })
      if (when) parts.push({ text: when, strong: true })
    }
  } else {
    parts.push({ text: `Creates ${listWords(made, Boolean(when))}` })
    if (when) parts.push({ text: when, strong: true })
  }
  return { kind: 'creates', parts }
}

/** *Renee, the residence, the job and* — the list, ready for one more bold item when there is one. */
function listWords(items: string[], more: boolean): string {
  if (more) return items.length === 0 ? '' : `${items.join(', ')} and `
  if (items.length <= 1) return items.join('')
  return `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`
}

/**
 * The footer in book mode (§11.4), where the contact, the place and the job
 * already exist and **Book** only gives the site visit a time — so it says
 * what Book does rather than what Create would make. Undrawn; the words follow
 * the sheet's own *Still needed:* and the bold *<when> with <who>*.
 */
export function bookNote(
  state: SheetState,
  shown: { salespersonFirstName: string | null; reference: string },
): FooterNote {
  const who = firstOf(shown.salespersonFirstName ?? state.appointment.salespersonName)
  if (!state.appointment.salespersonId) return { kind: 'needed', text: 'Still needed: who goes and a time' }
  if (state.appointment.date === null || state.appointment.start === null) {
    return { kind: 'needed', text: `Still needed: a time for ${who}` }
  }
  const when = `${dayComma(state.appointment.date).split(',')[0]} ${clockLabel(state.appointment.start)}`
  return { kind: 'creates', parts: [
    { text: 'Books ' }, { text: `${when} with ${who}`, strong: true }, { text: ` for ${shown.reference}` },
  ] }
}

/**
 * The name the sheet's sub-line carries (§3: added once one is chosen): what
 * was chosen first — *Kim Residence* when the place brought its people
 * (Board 3), the person when a person was chosen or typed in full (Boards 3 D
 * and 4) — and nothing while a contact is half typed (Board 9 A).
 */
export function subjectName(state: SheetState): string | null {
  if (state.property.kind === 'chosen' && (state.contactFromProperty || state.contact.kind === 'none')) {
    return propertyName(state)
  }
  if (state.contact.kind === 'chosen') return contactName(state)
  if (state.contact.kind === 'new' && contactStatus(state) === 'ready') return contactName(state)
  return state.property.kind === 'chosen' ? propertyName(state) : null
}

/** *10:00 AM* from minutes, for the footer. */
export function clockLabel(minutes: number): string {
  const hour = Math.floor(minutes / 60)
  return `${clockBare(minutes)} ${hour < 12 ? 'AM' : 'PM'}`
}

// ---------------------------------------------------------------------------
// What Create is missing (§11.3)
// ---------------------------------------------------------------------------

export interface Missing {
  section: SectionKey
  /** The field it belongs under, for the red hint. */
  field: 'lastName' | 'firstName' | 'address' | 'unit' | 'branch' | 'title' | 'division' | 'time'
    | 'contact'
  /** The red sentence under the field. */
  hint: string
  /** The words in *<n> things to finish: …*. */
  short: string
}

export function missingFor(
  state: SheetState, bookLater: boolean, salespersonFirstName: string | null,
): Missing[] {
  const out: Missing[] = []
  const want = needs(state)
  if (state.contact.kind === 'none') {
    out.push({ section: 'contact', field: 'contact', hint: 'Find a contact above, or add a new one.',
      short: 'a contact' })
  } else if (state.contact.kind === 'new') {
    if (!filled(state.contact.fields.firstName)) {
      out.push({ section: 'contact', field: 'firstName', hint: 'Add a first name — a contact needs one.',
        short: 'a first name' })
    }
    if (!filled(state.contact.fields.lastName)) {
      out.push({ section: 'contact', field: 'lastName', hint: 'Add a last name — a contact needs one.',
        short: 'a last name' })
    }
  }
  if (want.place) {
    if (state.property.kind === 'none'
      || (state.property.kind === 'new' && !filled(state.property.property.address.line1))) {
      out.push({ section: 'property', field: 'address', hint: 'Add the address.', short: 'an address' })
    } else if (state.property.kind === 'new') {
      const address = state.property.property.address
      if (address.needsUnit && !filled(address.line2)) {
        out.push({ section: 'property', field: 'unit', hint: 'Add the unit or lot.', short: 'a unit or lot' })
      } else if (!addressReady(address)) {
        out.push({ section: 'property', field: 'address', hint: 'Add the address.', short: 'an address' })
      } else if (!state.property.property.orgUnitId && !state.property.property.check?.branch) {
        out.push({ section: 'property', field: 'branch', hint: 'Choose a branch.', short: 'a branch' })
      }
    }
  }
  if (want.job) {
    if (!filled(state.job.title)) {
      out.push({ section: 'job', field: 'title', hint: 'Add a title.', short: 'a title' })
    }
    if (!state.job.divisionCode) {
      out.push({ section: 'job', field: 'division', hint: 'Choose a division.', short: 'a division' })
    }
  }
  if (want.visit && !bookLater && !(state.appointment.startsAt && state.appointment.salespersonId)) {
    const who = firstOf(salespersonFirstName ?? state.appointment.salespersonName)
    out.push(state.appointment.salespersonId
      ? { section: 'appointment', field: 'time', hint: `Pick a time for ${who} — or press`,
        short: `a time for ${who}` }
      : { section: 'appointment', field: 'time',
        hint: 'Choose who goes and a time — or press', short: 'who goes and a time' })
  }
  return out
}

export function missingWords(missing: Missing[]): string {
  return `${missing.length} thing${missing.length === 1 ? '' : 's'} to finish: `
}

// ---------------------------------------------------------------------------
// Summary lines for folded sections (§5.0)
// ---------------------------------------------------------------------------

export function budgetLabel(key: string): string {
  return BUDGET_BANDS.find((band) => band.key === key)?.label ?? 'Not sure'
}

/** *Hardscape · Yard sign · by Wed, Nov 25 · $10k–25k* (Board 7). */
export function jobSummary(
  job: JobFields,
  names: { division: string | null; leadSource: string | null; wanted: string | null },
): string {
  return [names.division, names.leadSource, names.wanted,
    job.budgetKey !== 'not_sure' ? budgetLabel(job.budgetKey) : null]
    .filter(Boolean).join(' · ')
}

/** The words a When choice will read as afterwards, from the chip list for today. */
export function wantedLabel(choice: WantedChoice | null, chips: WhenChip[]): string | null {
  if (!choice) return null
  if (choice.kind === 'season') {
    return chips.find((chip) => chip.key === `season:${choice.season}`)?.label ?? null
  }
  try {
    return wantedText({
      kind: choice.kind,
      start: choice.kind === 'between' || choice.kind === 'on' || choice.kind === 'after'
        ? choice.date ?? null : null,
      end: choice.kind === 'between' ? choice.date2 ?? null
        : choice.kind === 'by' || choice.kind === 'on' ? choice.date ?? null : null,
      label: null,
    })
  } catch {
    return null
  }
}

// ---------------------------------------------------------------------------
// Which way the confirmation goes (§12.1) — the one reading of the contact
// ---------------------------------------------------------------------------

/**
 * `confirmChannel` over whatever the Contact section holds: a chosen contact's
 * mobile, email, Prefers and STOP; a typed one's number when its kind is
 * Mobile. The sheet's switch and the create call both read this.
 */
export function channelFor(state: SheetState, smsReady: boolean): ConfirmChannel {
  if (state.contact.kind === 'chosen') {
    const known = state.contact.contact
    return confirmChannel({
      prefers: known.prefers, mobile: known.mobile, email: known.email,
      optedOut: known.optedOut, smsReady,
    })
  }
  if (state.contact.kind === 'new') {
    const fields = state.contact.fields
    return confirmChannel({
      prefers: fields.prefers,
      mobile: fields.phoneKind === 'mobile' && fields.phone.trim() ? fields.phone : null,
      email: fields.email.trim() || null,
      optedOut: false,
      smsReady,
    })
  }
  return { channel: null, to: null, band: 'no_recipient' }
}
